/* ═══════════════════════════════════════════════════════════════════════
   Signing up. An email and a password, and nothing else.

   It used to also ask for a trading name, a web address and an industry,
   because an account WAS a business. Those are three decisions to make in
   front of somebody who has not yet decided they want an account, and the
   first of them — what shall I call this — is the kind of question people
   close a tab over.

   They have not disappeared. They moved to the moment somebody makes a
   page, which is where they are actually being asked something. An account
   with no pages is a perfectly good account and it is what everyone has
   for the first minute.
   ═══════════════════════════════════════════════════════════════════════ */

import { useEffect, useRef, useState } from "react";
import { applyAccent, applyTheme, prefersDark, type Theme } from "./theme";
import { useHomeHref } from "./session";
import { DashboardLink } from "./components/chrome";
import { emailProblem, suggestEmail } from "./emails";
import { CraigFace } from "./components/CraigFace";
import { PasswordField } from "./components/PasswordField";
import { LetCraigDoIt, Wordmark } from "./components/Brand";

export function SignUp() {
  const [theme] = useState<Theme>(() => (prefersDark() ? "dark" : "light"));
  /* The mark is the way out. Someone already signed in who wandered back
     here wants their dashboard, not the pitch. */
  const home = useHomeHref();
  useEffect(() => {
    applyTheme(theme);
    applyAccent(undefined, theme);
  }, [theme]);

  const [email, setEmail] = useState("");
  const [emailAgain, setEmailAgain] = useState("");
  const [touchedEmail, setTouchedEmail] = useState(false);
  const [password, setPassword] = useState("");
  const [passwordAgain, setPasswordAgain] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await fetch("/api/account/sign-up", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const body = (await res.json().catch(() => ({}))) as { error?: string };
      if (!res.ok) {
        setError(body.error ?? "That didn't work.");
        return;
      }
      // To their pages, which is empty and says so. Making the first one
      // is the next thing, and it is one button.
      window.location.href = "/dashboard";
    } catch {
      setError("Couldn't reach us just now. Try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  /* Compared normalised, because the difference between "Dana@x.com" and
     "dana@x.com " is a trailing space and a capital, not a mistake worth
     stopping somebody over. The server lowercases and trims anyway. */
  const same = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();
  const emailsMatch = same(email, emailAgain);
  const passwordsMatch = password === passwordAgain;

  /* Shown once they've moved on, not while they're still typing the
     domain — "the part after the @ needs a dot in it" is unhelpful when
     they've got as far as "sam@gmail". */
  const emailBad = email && touchedEmail ? emailProblem(email) : null;
  const emailMaybe = !emailBad && email ? suggestEmail(email) : null;

  const ready =
    !emailProblem(email) &&
    emailsMatch &&
    password.length >= 10 &&
    passwordsMatch;

  return (
    <div className="min-h-screen flex flex-col">
      {/* Only for somebody who already has an account and wandered back
          here. A stranger making one gets no navigation off the form. */}
      {home === "/dashboard" && (
        <header style={{ borderBottom: "1px solid var(--line)", background: "var(--surface)" }}>
          <div className="max-w-4xl mx-auto px-5 py-3 flex items-center">
            <DashboardLink />
          </div>
        </header>
      )}
      <main className="flex-1 grid place-items-center px-5 py-10">
        <div className="w-full" style={{ maxWidth: 460 }}>
          <a
            href={home}
            className="flex flex-col items-center gap-2 mb-6"
            style={{ textDecoration: "none", color: "inherit" }}
            aria-label={home === "/dashboard" ? "Back to your dashboard" : "Back to the home page"}
          >
            <CraigFace size={44} />
            <Wordmark size={18} />
          </a>

          <div className="panel panel-ticks px-5 py-5">
            <h1 className="mono" style={{ fontSize: 17, fontWeight: 700, margin: "0 0 4px" }}>
              <span style={{ color: "var(--accent)" }}>&gt;</span> Create your account
            </h1>
            <p style={{ fontSize: 13.5, color: "var(--ink-2)", marginBottom: 16, lineHeight: 1.5 }}>
              Just an email and a password. You'll set up your first page next — as many as you
              like, and nothing is public until you say so.
            </p>

            <form
              className="space-y-3"
              onSubmit={(e) => {
                e.preventDefault();
                if (!busy && ready) void submit();
              }}
            >
              {/*
                Typed twice, and that is not belt-and-braces here.

                This address is the only way back into the account — a
                password reset goes to it and nowhere else. A typo does not
                fail loudly: the verification email simply never arrives,
                they cannot put a page live, and the account they built is
                unreachable. Catching it here costs ten seconds.

                Pasting is deliberately NOT blocked. Stopping people
                pasting into a confirm field is a habit that punishes
                password managers and helps nobody.
              */}
              <Field label="Your email">
                <input
                  className="field"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onBlur={() => setTouchedEmail(true)}
                />
                {emailBad && <Mismatch>{emailBad}</Mismatch>}
                {/* A suggestion, never a correction. Typing it for them
                    would be wrong the one time in ten thousand that the
                    odd-looking domain is really theirs. */}
                {emailMaybe && (
                  <p style={{ fontSize: 12.5, color: "var(--ink-3)", marginTop: 4 }}>
                    Did you mean{" "}
                    <button
                      type="button"
                      onClick={() => {
                        setEmail(emailMaybe);
                        setEmailAgain("");
                      }}
                      style={{
                        background: "none",
                        border: "none",
                        padding: 0,
                        font: "inherit",
                        color: "var(--accent)",
                        textDecoration: "underline",
                        cursor: "pointer",
                      }}
                    >
                      {emailMaybe}
                    </button>
                    ?
                  </p>
                )}
              </Field>

              <Field label="Confirm email" hint="It's the only way back into your account.">
                <input
                  className="field"
                  type="email"
                  autoComplete="email"
                  value={emailAgain}
                  onChange={(e) => setEmailAgain(e.target.value)}
                />
                {/* Only once they've typed something. Telling someone the
                    fields don't match while the second one is still empty
                    is an interface arguing with a person mid-word. */}
                {emailAgain && !emailsMatch && (
                  <Mismatch>Those two don't match.</Mismatch>
                )}
              </Field>

              <PasswordField
                id="signup-password"
                label="A password"
                value={password}
                onChange={setPassword}
              >
                <p style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 4, lineHeight: 1.45 }}>
                  Ten or more, with a capital, a lower-case letter and a number. Three unrelated
                  words and a digit beats something clever you won't remember.
                </p>
              </PasswordField>

              <PasswordField
                id="signup-password-again"
                label="Confirm password"
                hint="Reveal it if you'd rather check by eye."
                value={passwordAgain}
                onChange={setPasswordAgain}
              >
                {passwordAgain && !passwordsMatch && (
                  <Mismatch>Those two don't match.</Mismatch>
                )}
              </PasswordField>

              {error && (
                <p
                  role="alert"
                  style={{
                    fontSize: 13.5,
                    color: "var(--danger)",
                    background: "var(--warn-tint)",
                    border: "1px solid var(--warn)",
                    borderRadius: "var(--radius)",
                    padding: "0.5rem 0.7rem",
                  }}
                >
                  {error}
                </p>
              )}

              <button
                type="submit"
                className="btn btn-primary btn-term w-full"
                disabled={busy || !ready}
              >
                {busy ? "…" : "Create my account"}
              </button>
            </form>
          </div>

          <p style={{ textAlign: "center", fontSize: 13.5, color: "var(--ink-3)", marginTop: 18 }}>
            Already have one?{" "}
            <a href="/account/sign-in" style={{ color: "var(--accent)", fontWeight: 600 }}>
              Sign in
            </a>
          </p>
        </div>
      </main>
      <LetCraigDoIt />
    </div>
  );
}

/** Said quietly, beside the field, rather than as an alert at the top. */
function Mismatch({ children }: { children: React.ReactNode }) {
  return (
    <p style={{ fontSize: 12.5, color: "var(--danger)", marginTop: 4 }}>{children}</p>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  /** Sits under the label, before the box. Says why, never what. */
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="term-label" style={{ display: "block", marginBottom: hint ? 2 : 5 }}>
        {label}
      </span>
      {hint && (
        <span
          style={{ display: "block", fontSize: 12, color: "var(--ink-3)", marginBottom: 5 }}
        >
          {hint}
        </span>
      )}
      {children}
    </label>
  );
}
